package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.domain.model.UniverseConfig
import fr.manyverse.app.domain.repository.UniverseRepository
import fr.manyverse.app.domain.usecase.CreateUniverseUseCase
import fr.manyverse.app.domain.usecase.DeleteUniverseUseCase
import fr.manyverse.app.domain.usecase.UpdateUniverseUseCase
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class UniversesViewModel(
    private val universeRepository: UniverseRepository,
    private val createUniverseUseCase: CreateUniverseUseCase,
    private val updateUniverseUseCase: UpdateUniverseUseCase,
    private val deleteUniverseUseCase: DeleteUniverseUseCase
) : BaseViewModel() {

    private val _listState = MutableStateFlow<AsyncUiState<List<Universe>>>(AsyncUiState.Idle)
    val listState: StateFlow<AsyncUiState<List<Universe>>> = _listState.asStateFlow()

    private val _saveState = MutableStateFlow<AsyncUiState<Universe>>(AsyncUiState.Idle)
    val saveState: StateFlow<AsyncUiState<Universe>> = _saveState.asStateFlow()

    private val _deleteState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteState: StateFlow<AsyncUiState<Unit>> = _deleteState.asStateFlow()

    fun loadUniverses(userId: String) {
        launchAsync(_listState) {
            universeRepository.getCustomUniversesForUser(userId)
        }
    }

    /**
     * Refreshes the list without flipping it to Loading, so an already-displayed list
     * (e.g. a list-detail layout) is not replaced by a skeleton after a save/delete.
     */
    private fun reloadUniversesSilently(userId: String) {
        launchAsyncSilent(_listState) {
            universeRepository.getCustomUniversesForUser(userId)
        }
    }

    fun save(name: String, config: UniverseConfig, createdBy: String, editingId: String?) {
        viewModelScope.launch {
            launchAsync(_saveState) {
                if (editingId == null) {
                    createUniverseUseCase(name, config, createdBy)
                } else {
                    updateUniverseUseCase(editingId, name, config)
                }
            }.join()
            if (_saveState.value is AsyncUiState.Success) {
                reloadUniversesSilently(createdBy)
            }
        }
    }

    fun delete(id: String, userId: String) {
        viewModelScope.launch {
            launchAction(_deleteState) {
                deleteUniverseUseCase(id)
            }.join()
            if (_deleteState.value is AsyncUiState.Success) {
                reloadUniversesSilently(userId)
            }
        }
    }

    fun resetSaveState() = resetState(_saveState)
    fun resetDeleteState() = resetState(_deleteState)
}
